/**
 * Barrier for in-flight durable (IndexedDB) writes.
 *
 * Why this exists: the board engine applies an optimistic edit to the store and
 * THEN awaits `appendJournal` (`board-data/engine/engine.ts` → `syncer.enqueue`).
 * In that window the edit is on screen but not yet recoverable — a document swap
 * there loses it outright, which is exactly the failure mode SU spec requirement
 * 4 forbids across both the natural-boundary swap and the forced reload.
 *
 * The window is short, so the barrier is deliberately minimal: a write path marks
 * itself in flight, `updateLifecycle.applyUpdateNow()` waits for the count to
 * reach zero, and the swap proceeds. Nothing here retries or reorders writes —
 * once a write is journaled it already replays on the next board open.
 *
 * Not a general-purpose lock: it is a "don't navigate right now" signal only.
 */

import { reportError } from '@/lib/errors/reportError';

let inFlight = 0;
const waiters = new Set<() => void>();

/**
 * Mark a durable write as started. Returns the settle callback — call it in a
 * `finally` so a failed write still releases the barrier.
 */
export function beginDurableWrite(): () => void {
  inFlight += 1;
  let settled = false;
  return () => {
    if (settled) return;
    settled = true;
    inFlight -= 1;
    if (inFlight > 0) return;
    for (const waiter of [...waiters]) {
      waiter();
    }
  };
}

/** Number of durable writes currently in flight. */
export function durableWritesInFlight(): number {
  return inFlight;
}

/**
 * Resolve once no durable write is in flight, or after `timeoutMs`.
 *
 * The timeout is bounded rather than infinite because the caller is a document
 * swap — the forced path is an incident lever that has to land in seconds, and a
 * wedged IndexedDB connection must not be able to pin a user on a build we have
 * declared unsafe. Hitting it is reported, not swallowed: a write that never
 * settles is a real defect, and the report is how it becomes visible.
 */
export function whenDurableWritesSettled(timeoutMs: number): Promise<void> {
  if (inFlight === 0) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let done = false;
    const finish = (timedOut: boolean) => {
      if (done) return;
      done = true;
      waiters.delete(onSettled);
      clearTimeout(timer);
      if (timedOut) {
        reportError(
          new Error(`durable write barrier timed out with ${inFlight} write(s) in flight`),
          'durableWriteBarrier.timeout',
          { level: 'warning', extra: { inFlight, timeoutMs } },
        );
      }
      resolve();
    };
    const onSettled = () => finish(false);
    const timer = setTimeout(() => finish(true), timeoutMs);
    waiters.add(onSettled);
  });
}

